import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { Progress, Text } from '@mantine/core';
import { IconFileUpload } from '@tabler/icons-react';

import styles from './Dropzone.module.scss';

interface DropzoneProps {
  onFilesSelected: (files: FileList) => void;
  onPaste: () => void;
  disabled?: boolean;
  isUploading?: boolean;
  acceptedFileTypes?: string;
  maxFiles?: number;
  maxSize?: number; // optional max size in bytes
  label?: {
    line1: string;
    line2: string;
  };
}

const defaultLabel = { line1: 'Drop to upload', line2: 'Click to paste' };

// Helper to convert File[] to FileList
const FileListFromArray = (files: File[]): FileList => {
  const dataTransfer = new DataTransfer();
  files.forEach((file) => dataTransfer.items.add(file));
  return dataTransfer.files;
};

const Dropzone = ({
  onFilesSelected,
  onPaste,
  disabled = false,
  isUploading = false,
  acceptedFileTypes = 'image/*',
  maxFiles,
  maxSize,
  label = defaultLabel
}: DropzoneProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [isPdfAccepted, setIsPdfAccepted] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const prevUploadingRef = useRef(isUploading);

  useEffect(() => {
    setIsPdfAccepted(acceptedFileTypes.includes('application/pdf'));
  }, [acceptedFileTypes]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isUploading && !prevUploadingRef.current) {
      setProgress(0);

      intervalRef.current = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 99) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            return 99;
          }
          return prevProgress + 2;
        });
      }, 150);
    } else if (!isUploading && prevUploadingRef.current) {
      setProgress(100);

      timerRef.current = setTimeout(() => {
        setProgress(0);
      }, 500);
    }

    prevUploadingRef.current = isUploading;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isUploading]);

  const validateFiles = useCallback(
    (files: File[]): { validFiles: File[]; errors: string[] } => {
      const validFiles: File[] = [];
      const errors: string[] = [];

      // Check max files limit first
      if (maxFiles && files.length > maxFiles) {
        errors.push(
          `Too many files selected. Maximum allowed is ${maxFiles} file${maxFiles === 1 ? '' : 's'}.`
        );
        // If maxFiles is exceeded, take only the first maxFiles
        files = files.slice(0, maxFiles);
      }

      for (const file of files) {
        if (maxSize && file.size > maxSize) {
          errors.push(
            `${file.name} - File too large. Maximum size is ${(maxSize / (1024 * 1024)).toFixed(1)} MB`
          );
        } else {
          validFiles.push(file);
        }
      }

      return { validFiles, errors };
    },
    [maxFiles, maxSize]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setFileError(null);

      if (disabled || isUploading) return;

      const { files } = e.dataTransfer;
      if (files && files.length > 0) {
        const { validFiles, errors } = validateFiles(Array.from(files));

        if (errors.length > 0) {
          setFileError(errors.join(' '));
        }

        if (validFiles.length > 0) {
          onFilesSelected(FileListFromArray(validFiles));
        }
      }
    },
    [disabled, isUploading, onFilesSelected, validateFiles]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setFileError(null);

      const { files } = e.target;
      if (!files || files.length === 0) return;

      const { validFiles, errors } = validateFiles(Array.from(files));

      if (errors.length > 0) {
        setFileError(errors.join(' '));
      }

      if (validFiles.length > 0) {
        onFilesSelected(FileListFromArray(validFiles));
      }

      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [onFilesSelected, validateFiles]
  );

  const handleClick = useCallback(() => {
    if (disabled || isUploading) {
      return;
    }

    if (isPdfAccepted) {
      // If PDF is accepted, open file browser instead of pasting
      inputRef.current?.click();
    } else {
      // Otherwise use the paste functionality
      onPaste();
    }
  }, [onPaste, disabled, isUploading, isPdfAccepted]);

  const renderContent = () => {
    if (isUploading) {
      return (
        <Progress
          value={progress}
          size="md"
          radius="sm"
          striped
          animated
          className={styles.progressBar}
        />
      );
    }

    // If PDF is accepted, change the second line of text
    const line2Text = isPdfAccepted ? 'Click to browse' : label.line2;

    return (
      <>
        <Text className={styles.labelText}>{label.line1}</Text>
        <Text className={styles.labelText}>{line2Text}</Text>
      </>
    );
  };

  return (
    <>
      <div
        className={`${styles.dropzone} ${isDragging ? styles.dragging : ''} ${disabled ? styles.disabled : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}>
        <IconFileUpload size={34} stroke={1} className={styles.fileUploadImg} />
        {renderContent()}
      </div>

      {fileError && (
        <Text style={{ color: 'red' }} size="sm" mt={4}>
          {fileError}
        </Text>
      )}

      <input
        type="file"
        hidden
        accept={acceptedFileTypes}
        ref={inputRef}
        multiple={maxFiles !== 1}
        onChange={handleInputChange}
        disabled={disabled || isUploading}
      />
    </>
  );
};

export default Dropzone;
